import React, { useState, useEffect } from 'react';
import { Card, DatePicker, Typography, Tag, Avatar, Spin} from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import Table from '../../shared/dataTable';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import breakService from '../../../services/break/break.service';
import { BreakHistoryCompanyDto, BreakHistoryDto } from '../../../types/break.types';
import { BreakType } from '../../../constants/breakConstant';
import { createPdf, exportToExcel, normalizeTurkishChars } from '../../../utils/exportUtils';
import BreakFilters from './components/BreakFilters';
import departmentService from '../../../services/department/department.service';
import shopService from '../../../services/shop/shop.service';
import { DepartmentDto } from '../../../types/department.types';
import { Store } from '../../../types/shop.types';
import { getFileUrl } from '../../../utils/file.utils';
import { handleApiError } from '../../../utils/errorHandler';
import { useTranslation } from 'react-i18next';
import FooterSection from '../../shared/components/footerSection';
import i18n from '../../../i18n/config';
import { Cell, ResponsiveContainer, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from 'recharts';
import { PermissionType } from '../../../types/user.types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';

// Day.js ayarları
dayjs.locale('tr');
dayjs.extend(localizedFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);


const BreakReport: React.FC = () => {
  // State tanımlamaları
  const [breakHistory, setBreakHistory] = useState<BreakHistoryCompanyDto | null>(null);
  const [filteredBreaks, setFilteredBreaks] = useState<BreakHistoryDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [shops, setShops] = useState<Store[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string[]>([]); // Değişiklik: string[]
  const [selectedShop, setSelectedShop] = useState<string[]>([]); // Değişiklik: string[]
  const [selectedGender, setSelectedGender] = useState<string>('all');
  const [selectedDuration, setSelectedDuration] = useState<string | null>('all');
  const [selectedBreakType, setSelectedBreakType] = useState<string | null>('all');
  const {t} = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  // Verileri getiren fonksiyon
  const fetchBreakHistory = async (startDate: string, endDate: string) => {
    setLoading(true);
    try {
      const response = await breakService.getBreakHistoryCompany(startDate, endDate);
      const data = Array.isArray(response) ? response[0] : response;
      const sortedData = data.breaks.sort((a, b) => dayjs(b.startTime).diff(dayjs(a.startTime)));
      setBreakHistory(data);
      setFilteredBreaks(sortedData || []);
    } catch (error: any) {
      handleApiError(error,t);
    } finally {
      setLoading(false);
    }
  };

  // Departman ve şube verilerini getir
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [deptData, shopData] = await Promise.all([
          departmentService.getDepartments(),
          shopService.getShops()
        ]);
        setDepartments(deptData);
        setShops(shopData);
      } catch (error: any) {
        handleApiError(error,t);
      }
    };
    fetchData();
  }, []);

  // Sayfa yüklendiğinde verileri getir
  useEffect(() => {
    const startDate = dayjs().startOf('day').format('YYYY-MM-DD');
    const endDate = dayjs().endOf('day').format('YYYY-MM-DD');
    setDateRange([dayjs(startDate), dayjs(endDate)]);

    fetchBreakHistory(startDate, endDate);
  }, []);

  // Filtreleri uygula
  const applyFilters = () => {
    if (!breakHistory) return;
    
    let result = [...breakHistory.breaks];

    // Departman filtresi (çoklu)
    if (selectedDepartment.length > 0) {
      const selectedDepartmentNames = departments
        .filter(d => selectedDepartment.includes(d.id.toString()))
        .map(d => d.name);
      result = result.filter(breakRecord => 
        selectedDepartmentNames.includes(breakRecord.department)
      );
    }

    // Şube filtresi (çoklu)
    if (selectedShop.length > 0) {
      const selectedShopNames = shops
        .filter(s => selectedShop.includes(s.id.toString()))
        .map(s => s.name);
      result = result.filter(breakRecord => 
        selectedShopNames.includes(breakRecord.branch)
      );
    }

    if (dateRange) {
      const [start, end] = dateRange;
      result = result.filter(breakRecord => {
        const recordDate = dayjs(breakRecord.startTime);
        return recordDate.isSameOrAfter(start, 'day') && recordDate.isSameOrBefore(end, 'day');
      });
    }

    if(selectedGender !== 'all'){
      result = result.filter(breakRecord => 
        breakRecord.gender === selectedGender
      );
    }

    if (selectedBreakType !== 'all') {
      result = result.filter(breakRecord => {
        const breakTypeKey = Object.entries(BreakType).find(([_, value]) => value === breakRecord.type)?.[0];
        return breakTypeKey && breakRecord.type === selectedBreakType;
      });
    }

    if (selectedDuration !== 'all') {
      result = result.filter(breakRecord => {
        const duration = breakRecord.breakDuration;
        switch (selectedDuration) {
          case '15':
            return duration <= 15;
          case '30':
            return duration > 15 && duration <= 30;
          case '60':
            return duration > 30 && duration <= 60;
          case '60+':
            return duration > 60;
          default:
            return true;
        }
      });
    }

    setFilteredBreaks(result);
  };

  useEffect(() => {
    applyFilters();
  }, [selectedDepartment, selectedShop, selectedBreakType, selectedDuration, dateRange, breakHistory, selectedGender]);

  // Filtreleri sıfırla
  const handleReset = () => {
    setDateRange(null);
    setSelectedDepartment([]); // Değişiklik: []
    setSelectedShop([]); // Değişiklik: []
    setSelectedGender('all');
    setSelectedBreakType('all');
    setSelectedDuration('all');
    if (breakHistory) {
      setFilteredBreaks([...breakHistory.breaks]);
    } else {
      setFilteredBreaks([]);
    }
  };

  // Tarihi formatla
  const formatTime = (time: string) => {
    if (!time) return '-';
    return dayjs(time).format('HH:mm:ss');
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return dayjs(date).format('DD MMMM YYYY');
  };

  const formatDuration = (minutes: number) => {
    if (minutes === 0) return t('breakReport.table.zero');
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours} ${t('breakReport.table.hours')} ${mins} ${t('breakReport.table.minutes')}` : `${mins} ${t('breakReport.table.minutes')}`;
  };

  // PDF dışa aktarma
  const handleExportPdf = () => {
    type BreakDataType = {
      userName: string;
      department: string;
      branch: string;
      date: string;
      breakType: string;
      startTime: string;
      endTime: string;
      duration: string;
    };

    // PDF için sütun tanımları - Landscape A4 genişliğine göre optimize edildi (297mm, margin'lerle ~281mm)
    const columnsForPdf = [
      { header: normalizeTurkishChars('Ad Soyad'), property: 'userName' as keyof BreakDataType, width: 50, align: 'left' as const },
      { header: normalizeTurkishChars('Departman'), property: 'department' as keyof BreakDataType, width: 40, align: 'left' as const },
      { header: normalizeTurkishChars('Şube'), property: 'branch' as keyof BreakDataType, width: 35, align: 'left' as const },
      { header: normalizeTurkishChars('Tarih'), property: 'date' as keyof BreakDataType, width: 30, align: 'center' as const },
      { header: normalizeTurkishChars('Mola Tipi'), property: 'breakType' as keyof BreakDataType, width: 40, align: 'left' as const },
      { header: normalizeTurkishChars('Başlangıç'), property: 'startTime' as keyof BreakDataType, width: 30, align: 'center' as const },
      { header: normalizeTurkishChars('Bitiş'), property: 'endTime' as keyof BreakDataType, width: 30, align: 'center' as const },
      { header: normalizeTurkishChars('Süre'), property: 'duration' as keyof BreakDataType, width: 25, align: 'center' as const }
    ];

    // Mola tipi Türkçe karşılıkları - Türkçe karakter içermeyen versiyonlar
    const breakTypeLabelsForPdf: Record<string, string> = {
      ["short_break"]: normalizeTurkishChars(t('Kısa Mola')),
      ["food_break"]: normalizeTurkishChars(t('Yemek Mola')),
      ["personal_break"]: normalizeTurkishChars(t('Kişisel Mola')),
      ["other_break"]: normalizeTurkishChars(t('Diğer Mola'))
    };
    // console.log(breakTypeLabelsForPdf);
    // PDF için formatlanmış veri
    const formattedData = filteredBreaks.map((record: BreakHistoryDto) => ({
      userName: normalizeTurkishChars(record.userName || '-'),
      department: normalizeTurkishChars(record.department || '-'),
      branch: normalizeTurkishChars(record.branch || '-'),
      date: formatDate(record.startTime),
      breakType: normalizeTurkishChars(breakTypeLabelsForPdf[record.type] || record.type),
      startTime: formatTime(record.startTime),
      endTime: formatTime(record.endTime),
      duration: formatDuration(record.breakDuration)
    }));

    // Mola istatistikleri
    const totalBreakCount = getTotalBreakCount();
    const avgBreakDuration = getAverageBreakDuration();
    const activeBreakCount = getActiveBreakCount();
    const totalBreakDuration = getTotalBreakDuration();

    // İstatistik bilgileri için uygun format - Türkçe karakter desteği ile
    const statistics = [
      { label: normalizeTurkishChars('Toplam Mola Sayısı'), value: totalBreakCount.toString() },
      { label: normalizeTurkishChars('Ortalama Mola Süresi'), value: formatDuration(avgBreakDuration) },
      { label: normalizeTurkishChars('Aktif Mola Sayısı'), value: activeBreakCount.toString() },
      { label: normalizeTurkishChars('Toplam Mola Süresi'), value: formatDuration(totalBreakDuration) }
    ];

    // Filtre açıklaması oluştur
    let description = normalizeTurkishChars('Mola Kayıtları Raporu');
    
    if (dateRange) {
      const [start, end] = dateRange;
      description += ` - Tarih: ${start.format('DD.MM.YYYY')} - ${end.format('DD.MM.YYYY')}`;
    }
    
    if (selectedDepartment.length > 0) {
      const deptNames = departments
        .filter(d => selectedDepartment.includes(d.id.toString()))
        .map(d => d.name)
        .join(', ');
      description += ` - Departman: ${normalizeTurkishChars(deptNames)}`;
    }
    
    if (selectedShop.length > 0) {
      const shopNames = shops
        .filter(s => selectedShop.includes(s.id.toString()))
        .map(s => s.name)
        .join(', ');
      description += ` - Şube: ${normalizeTurkishChars(shopNames)}`;
    }

    if (selectedBreakType !== 'all') {
      const breakTypeName = selectedBreakType?.replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ç/g, 'c') || '';
      description += ` - Mola Tipi: ${normalizeTurkishChars(breakTypeName)}`;
    }


    // createPdf fonksiyonunu çağır
    createPdf(formattedData, {
      title: normalizeTurkishChars('Mola Kayıtları Raporu'),
      description: description,
      headerColor: '#2c3e50', // Koyu lacivert
      accentColor: '#3498db', // Mavi
      columns: columnsForPdf,
      showStatistics: true,
      statistics: statistics,
      footer: {
        text: `© ${new Date().getFullYear()} ${normalizeTurkishChars('DINOX')} - ${normalizeTurkishChars('Tüm Hakları Saklıdır')}`,
        showPageNumbers: true
      }
    });
  };

  // Mola adedi bazında sıralama yapan fonksiyon
  const getTopBreakUsersChartData = () => {
    const userStats: Record<string, { count: number; duration: number }> = {};
    
    // Filtrelenmiş verilerden kullanıcı istatistiklerini hesapla
    filteredBreaks.forEach(breakRecord => {
      const userName = breakRecord.userName || 'Bilinmeyen';
      if (!userStats[userName]) {
        userStats[userName] = { count: 0, duration: 0 };
      }
      userStats[userName].count += 1;
      userStats[userName].duration += breakRecord.breakDuration;
    });

    // Kullanıcı istatistiklerini döndür - Mola sayısına göre sırala
    return Object.entries(userStats)
      .map(([name, stats]) => ({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        fullName: name, // Tooltip için tam isim
        count: stats.count,
        duration: stats.duration
      }))
      .sort((a, b) => b.count - a.count) // Mola sayısına göre sırala
      .slice(0, 15); // İlk 15 kişiyi göster
  };

  // Mola süresi bazında sıralama yapan fonksiyon
  const getTopBreakUsersByDurationChartData = () => {
    const userStats: Record<string, { count: number; duration: number }> = {};
    
    // Filtrelenmiş verilerden kullanıcı istatistiklerini hesapla
    filteredBreaks.forEach(breakRecord => {
      const userName = breakRecord.userName || 'Bilinmeyen';
      if (!userStats[userName]) {
        userStats[userName] = { count: 0, duration: 0 };
      }
      userStats[userName].count += 1;
      userStats[userName].duration += breakRecord.breakDuration;
    });

    // Kullanıcı istatistiklerini döndür - Mola süresine göre sırala
    return Object.entries(userStats)
      .map(([name, stats]) => ({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        fullName: name, // Tooltip için tam isim
        count: stats.count,
        duration: stats.duration
      }))
      .sort((a, b) => b.duration - a.duration) // Mola süresine göre sırala (EN ÖNEMLİ DEĞİŞİKLİK)
      .slice(0, 15); // İlk 15 kişiyi göster
  };

  const getHourlyBreakDistribution = () => {
    const hourlyData: Record<number, number> = {};
    
    // Filtrelenmiş verilerden saatlik dağılımı hesapla
    filteredBreaks.forEach(breakRecord => {
      const hour = dayjs(breakRecord.startTime).hour();
      hourlyData[hour] = (hourlyData[hour] || 0) + 1;
    });

    // 24 saatlik dizi oluştur, sadece veri olan saatleri göster
    return Array.from({ length: 24 }, (_, hour) => ({
      hour: `${hour.toString().padStart(2, '0')}:00`,
      count: hourlyData[hour] || 0,
      hourNumber: hour
    })).filter(item => item.count > 0);
  };

  // Excel dışa aktarma
  const handleExportExcel = () => {
    type ExcelColumns = {
      header: string;
      property: 'userName' | 'department' | 'branch' | 'date' | 'breakType' | 'startTime' | 'endTime' | 'duration' | 'breakId' | 'userId';
      width?: number;
    };

    const columnsForExcel: ExcelColumns[] = [
      { header: 'Çalışan', property: 'userName' },
      { header: 'Departman', property: 'department' },
      { header: 'Şube', property: 'branch' },
      { header: 'Tarih', property: 'date' },
      { header: 'Mola Tipi', property: 'breakType' },
      { header: 'Başlangıç', property: 'startTime' },
      { header: 'Bitiş', property: 'endTime' },
      { header: 'Süre (dk)', property: 'duration' },
      { header: 'Mola ID', property: 'breakId' },
      { header: 'Kullanıcı ID', property: 'userId' }
    ];

    const data = filteredBreaks.map((record: BreakHistoryDto) => ({
      userName: record.userName || '-',
      department: record.department || '-',
      branch: record.branch || '-',
      date: formatDate(record.startTime),
      breakType: t(`breakTypes.${record.type}`),
      startTime: formatTime(record.startTime),
      endTime: formatTime(record.endTime),
      duration: record.breakDuration,
      breakId: record.breakId,
      userId: record.userId
    }));

    exportToExcel(data, 'Mola Kayitlari Raporu', columnsForExcel);
  };

  // İstatistikler için yardımcı fonksiyonlar
  const getTotalBreakCount = () => filteredBreaks.length;

  const getAverageBreakDuration = () => {
    const totalDuration = filteredBreaks.reduce((acc, curr) => acc + curr.breakDuration, 0);
    return Math.round(totalDuration / (filteredBreaks.length || 1));
  };

  const getActiveBreakCount = () => filteredBreaks.filter(break_ => !break_.endTime).length;

  const getTotalBreakDuration = () => filteredBreaks.reduce((acc, curr) => acc + curr.breakDuration, 0);

  // Tablo sütunları
  const columns = [
    {
      title: t('breakReport.table.employee'),
      dataIndex: 'userName',
      key: 'userName',
      render: (text: string, record: BreakHistoryDto) => (
        <div className="d-flex align-items-center">
          <div className="avatar me-2">
            {record.profilePicture ? (
              <Avatar src={getFileUrl(record.profilePicture)} size="large" />
            ) : (
              <Avatar src={record?.gender === 'male' ? '/assets/img/man.png' : '/assets/img/woman.png'} size="large" />
            )}
          </div>
          <div>
            <Link to={`/user-break?user=${record.userId}`}>
              <h6 className="mb-0">{text}</h6>
              <small className="text-muted">{record.department}</small>
            </Link>
          </div>
        </div>
      ),
    },
    {
      title: t('breakReport.table.branch'),
      dataIndex: 'branch',
      key: 'branch',
    },
    {
      title: t('breakReport.table.breakType'),
      dataIndex: 'type',
      key: 'type',
      render: (text: string) => {
        const colorMap: Record<string, string> = {
          [BreakType.SHORT_BREAK]: 'blue',
          [BreakType.LUNCH]: 'green',
          [BreakType.PERSONAL]: 'orange',
          [BreakType.OTHER]: 'geekblue'
        };
        
        return (
          <Tag color={colorMap[text] || 'default'}>
            {t(`breakTypes.${text}`)}
          </Tag>
        );
      },
    },
    {
      title: t('breakReport.table.date'),
      dataIndex: 'startTime',
      key: 'date',
      render: (text: string) => formatDate(text),
      sorter: (a: BreakHistoryDto, b: BreakHistoryDto) => dayjs(b.startTime).diff(dayjs(a.startTime)),
    },
    {
      title: t('breakReport.table.start'),
      dataIndex: 'startTime',
      key: 'startTime',
      render: (text: string) => formatTime(text),
      // sorter: (a: BreakHistoryDto, b: BreakHistoryDto) => dayjs(b.startTime).diff(dayjs(a.startTime)),
    },
    {
      title: t('breakReport.table.end'),
      dataIndex: 'endTime',
      key: 'endTime',
      render: (text: string) => formatTime(text),
    },
    {
      title: t('breakReport.table.duration'),
      dataIndex: 'breakDuration',
      key: 'breakDuration',
      render: (minutes: number) => formatDuration(minutes),
    }
  ];

  const handleBreakTypeChange = (type: string | null) => {
    setSelectedBreakType(type || 'all');
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-1">
          <div className="my-auto mb-2">
            <h2 className="mb-1">{t('breakReport.pageTitle')}</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">{t('breakReport.breadcrumb.reports')}</li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t('breakReport.pageTitle')}
                </li>
              </ol>
            </nav>
          </div>
          {user?.permissions?.includes(PermissionType.EXPORT) && (
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="dropdown me-3">
              <Link
                to="#"
                className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i className="ti ti-file-export me-1" />
                {t('common.buttons.export')}
              </Link>
              <ul className="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <Link
                    to="#"
                    className="dropdown-item rounded-2 d-flex align-items-center py-2"
                    onClick={()=>handleExportPdf()}
                  >
                    <i className="ti ti-file-type-pdf me-2 text-danger" />
                    {t('common.buttons.pdf')}
                  </Link>
                </li>
                <li className="mt-1">
                  <Link
                    to="#"
                    className="dropdown-item rounded-2 d-flex align-items-center py-2"
                    onClick={()=>handleExportExcel()}
                  >
                    <i className="ti ti-file-type-xls me-2 text-success" />
                    {t('common.buttons.excel')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          )}
        </div>
        <div className="col-md-12 text-end mb-2">
                  
                  <BreakFilters
                      selectedDateRange={dateRange ? [dateRange[0].toISOString(), dateRange[1].toISOString()] : null}
                      selectedDepartment={selectedDepartment}
                      selectedShop={selectedShop}
                      selectedBreakType={selectedBreakType}
                      selectedGender={selectedGender}
                      selectedDuration={selectedDuration}
                      departments={departments}
                      shops={shops}
                      onDateRangeChange={(dates) => {
                        if (dates) {
                          const [start, end] = dates;
                          setDateRange([dayjs(start), dayjs(end)]);
                          fetchBreakHistory(dayjs(start).format('YYYY-MM-DD'), dayjs(end).format('YYYY-MM-DD'));
                        } else {
                          setDateRange(null);
                          const today = dayjs();
                          fetchBreakHistory(
                            today.startOf('day').format('YYYY-MM-DD'),
                            today.endOf('day').format('YYYY-MM-DD')
                          );
                        }
                      }}
                      onDepartmentChange={setSelectedDepartment}
                      onShopChange={setSelectedShop}
                      onBreakTypeChange={handleBreakTypeChange}
                      onGenderChange={setSelectedGender}
                      onDurationChange={setSelectedDuration}
                      onReset={handleReset}
                    />
                  </div>
        {/* İstatistikler */}
        <div className="row">
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="card">
              <div className="card-body">
                <div className="dash-widget-header">
                  <div className="avatar bg-light-primary">
                    <i className="ti ti-coffee text-primary fs-20" />
                  </div>
                  <div className="dash-count">
                    <div className="dash-title">{t('breakReport.totalBreaks')}</div>
                    <div className="dash-counts">
                      <h4>{getTotalBreakCount()}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="card">
              <div className="card-body">
                <div className="dash-widget-header">
                  <div className="avatar bg-light-warning">
                    <i className="ti ti-clock text-warning fs-20" />
                  </div>
                  <div className="dash-count">
                    <div className="dash-title">{t('breakReport.averageBreakDuration')}</div>
                    <div className="dash-counts">
                      <h4>{formatDuration(getAverageBreakDuration())}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="card">
              <div className="card-body">
                <div className="dash-widget-header">
                  <div className="avatar bg-light-success">
                    <i className="ti ti-users text-success fs-20" />
                  </div>
                  <div className="dash-count">
                    <div className="dash-title">{t('breakReport.employeesOnBreak')}</div>
                    <div className="dash-counts">
                      <h4>{getActiveBreakCount()}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="card">
              <div className="card-body">
                <div className="dash-widget-header">
                  <div className="avatar bg-light-info">
                    <i className="ti ti-chart-pie text-info fs-20" />
                  </div>
                  <div className="dash-count">
                    <div className="dash-title">{t('breakReport.totalBreakDuration')}</div>
                    <div className="dash-counts">
                      <h4>{formatDuration(getTotalBreakDuration())}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <div>
                  <h5 className="card-title d-flex align-items-center mb-1">
                    <i className="ti ti-chart-bar me-2 text-primary"></i>
                    En Çok Mola Alan Çalışanlar (Adet)
                  </h5>
                  <p className="text-muted mb-0">Mola adedi bazında sıralama</p>
                </div>
              </div>
              <div className="card-body">
                {getTopBreakUsersChartData().length === 0 ? (
                  <div className="text-center py-5">
                    <i className="ti ti-chart-bar text-muted" style={{ fontSize: '48px' }}></i>
                    <p className="text-muted mt-3">Seçilen filtrelere göre mola verisi bulunamadı</p>
                  </div>
                ) : (
                  <div style={{ width: '100%', height: '400px' }}>
                    <ResponsiveContainer>
                      <BarChart 
                        data={getTopBreakUsersChartData()} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="name" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval={0}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis 
                          label={{ 
                            value: 'Mola Sayısı', 
                            angle: -90, 
                            position: 'insideLeft' 
                          }}
                        />
                        <RechartsTooltip 
                          formatter={(value, name, props) => {
                            const data = props.payload;
                            return [
                              `${value} mola`,
                              `${data?.fullName || 'Kullanıcı'}`
                            ];
                          }}
                          labelFormatter={(label) => {
                            const userData = getTopBreakUsersChartData().find(item => item.name === label);
                            const fullName = userData?.fullName || label;
                            const additionalInfo = `(Toplam süre: ${formatDuration(userData?.duration || 0)})`;
                            return `${fullName} ${additionalInfo}`;
                          }}
                        />
                        <Bar 
                          dataKey="count" 
                          fill="#1890ff" 
                          radius={[4, 4, 0, 0]}
                          name="Mola Sayısı"
                        >
                          {getTopBreakUsersChartData().map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={
                                index === 0 ? '#ff4d4f' : // En çok kullanan kırmızı
                                index === 1 ? '#fa8c16' : // İkinci turuncu  
                                index === 2 ? '#faad14' : // Üçüncü sarı
                                '#1890ff' // Diğerleri mavi
                              } 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <div>
                  <h5 className="card-title d-flex align-items-center mb-1">
                    <i className="ti ti-clock me-2 text-warning"></i>
                    En Çok Mola Alan Çalışanlar (Süre)
                  </h5>
                  <p className="text-muted mb-0">Toplam mola süresi bazında sıralama</p>
                </div>
              </div>
              <div className="card-body">
                {getTopBreakUsersByDurationChartData().length === 0 ? (
                  <div className="text-center py-5">
                    <i className="ti ti-clock text-muted" style={{ fontSize: '48px' }}></i>
                    <p className="text-muted mt-3">Seçilen filtrelere göre mola verisi bulunamadı</p>
                  </div>
                ) : (
                  <div style={{ width: '100%', height: '400px' }}>
                    <ResponsiveContainer>
                      <BarChart 
                        data={getTopBreakUsersByDurationChartData()} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="name" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval={0}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis 
                          label={{ 
                            value: 'Mola Süresi (dk)', 
                            angle: -90, 
                            position: 'insideLeft' 
                          }}
                        />
                        <RechartsTooltip 
                          formatter={(value, name, props) => {
                            const data = props.payload;
                            const hours = Math.floor(Number(value) / 60);
                            const minutes = Number(value) % 60;
                            const timeStr = hours > 0 ? `${hours}sa ${minutes}dk` : `${minutes}dk`;
                            return [
                              timeStr,
                              `${data?.fullName || 'Kullanıcı'}`
                            ];
                          }}
                          labelFormatter={(label) => {
                            const userData = getTopBreakUsersByDurationChartData().find(item => item.name === label);
                            const fullName = userData?.fullName || label;
                            const additionalInfo = `(Toplam ${userData?.count || 0} mola)`;
                            return `${fullName} ${additionalInfo}`;
                          }}
                        />
                        <Bar 
                          dataKey="duration" 
                          fill="#faad14" 
                          radius={[4, 4, 0, 0]}
                          name="Mola Süresi"
                        >
                          {getTopBreakUsersByDurationChartData().map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={
                                index === 0 ? '#ff4d4f' : // En çok kullanan koyu kırmızı (en yüksek süre)
                                index === 1 ? '#fa8c16' : // İkinci turuncu  
                                index === 2 ? '#faad14' : // Üçüncü sarı
                                '#faad14' // Diğerleri sarı
                              } 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
 {getHourlyBreakDistribution().length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title d-flex align-items-center mb-0">
                    <i className="ti ti-clock me-2 text-info"></i>
                    Genel Saatlik Mola Dağılımı
                  </h5>
                  <p className="text-muted mb-0">Hangi saatlerde en çok mola kullanıldığı</p>
                </div>
                <div className="card-body">
                  <div style={{ width: '100%', height: '350px' }}>
                    <ResponsiveContainer>
                      <BarChart 
                        data={getHourlyBreakDistribution()}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="hour" 
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis 
                          label={{ 
                            value: 'Mola Sayısı', 
                            angle: -90, 
                            position: 'insideLeft' 
                          }}
                        />
                        <RechartsTooltip 
                          formatter={(value) => [`${value} mola`, 'Mola Sayısı']}
                          labelFormatter={(label) => `${label} saati`}
                        />
                        <Bar 
                          dataKey="count" 
                          fill="#faad14" 
                          radius={[4, 4, 0, 0]}
                        >
                          {getHourlyBreakDistribution().map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={
                                entry.count >= Math.max(...getHourlyBreakDistribution().map(item => item.count)) * 0.8 
                                  ? '#ff4d4f' // En yoğun saatler kırmızı
                                  : entry.count >= Math.max(...getHourlyBreakDistribution().map(item => item.count)) * 0.6 
                                  ? '#fa8c16' // Orta yoğunluk turuncu
                                  : '#faad14' // Düşük yoğunluk sarı
                              } 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Filtreler ve Tablo */}
        <div className="card">
        {dateRange ? (
            <div className="card-body border-bottom pt-2 pb-2 bg-light">
              <div className="d-flex align-items-center">
                <i className="ti ti-calendar-event fs-16 me-2 text-primary"></i>
                <span className="fw-semibold text-muted me-1">{t('common.labels.dateRange', 'Tarih Aralığı')}:</span>
                <span className="text-dark fw-bold">
                  {dateRange[0].locale(i18n.language).format('DD MMMM YYYY')} - {dateRange[1].locale(i18n.language).format('DD MMMM YYYY')}
                </span>
              </div>
            </div>
          ) : (
            <div className="card-body border-bottom pt-2 pb-2 bg-light">
              <div className="d-flex align-items-center">
                <i className="ti ti-calendar-event fs-16 me-2 text-primary"></i>
                <span className="fw-semibold text-muted me-1">{t('common.labels.dateRange', 'Tarih Aralığı')}:</span>
                <span className="text-dark fw-bold">
                  {dayjs().startOf('day').locale(i18n.language).format('DD MMMM YYYY')} - {dayjs().endOf('day').locale(i18n.language).format('DD MMMM YYYY')}
                </span>
              </div>
            </div>
          )}
          <div className="card-body">
            <div className="card-title">
              <div className="row align-items-center">
                <div className="col-md-6">
                </div>
                
                
              </div>
            </div>
              <div className="table-responsive">
                <Spin spinning={loading}>
                  <Table
                    columns={columns} 
                    dataSource={filteredBreaks} 
                  />
                </Spin>
              </div>
            </div>
        </div>
      </div>
      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
            <FooterSection />
        </div>
    </div>
  );
};

export default BreakReport; 